const STORAGE_KEY = "signup_attribution_v1";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const SIGNUP_ATTRIBUTION_KEYS = [
  "meta_campaign_id",
  "meta_adset_id",
  "meta_ad_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "rdt_cid",
  "click_id",
  "reddit_click_id",
] as const;
export type SignupAttribution = Partial<Record<(typeof SIGNUP_ATTRIBUTION_KEYS)[number], string>> & {
  captured_at: string;
};
let memorySnapshot: SignupAttribution | null = null;

export function parseSignupAttribution(value: unknown): SignupAttribution | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  if (typeof input.captured_at !== "string") return null;
  const timestamp = Date.parse(input.captured_at);
  if (!Number.isFinite(timestamp) || timestamp > Date.now() || Date.now() - timestamp > TTL_MS) return null;
  const result: SignupAttribution = { captured_at: input.captured_at };
  for (const key of SIGNUP_ATTRIBUTION_KEYS) {
    const item = input[key];
    if (typeof item === "string" && item.trim()) result[key] = item.trim().slice(0, 512);
  }
  return Object.keys(result).length > 1 ? result : null;
}

export function getSignupAttribution(): SignupAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return parseSignupAttribution(JSON.parse(stored));
  } catch {
    /* Storage can be unavailable in in-app browsers. */
  }
  return parseSignupAttribution(memorySnapshot);
}

export function captureSignupAttribution(): SignupAttribution | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("utm_medium") === "email") return getSignupAttribution();
  const existing = getSignupAttribution();
  if (existing) return existing;
  const candidate: SignupAttribution = { captured_at: new Date().toISOString() };
  for (const key of SIGNUP_ATTRIBUTION_KEYS) {
    const value = params.get(key)?.trim();
    if (value) candidate[key] = value.slice(0, 512);
  }
  const snapshot = parseSignupAttribution(candidate);
  if (!snapshot) return null;
  memorySnapshot = snapshot;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* Keep this tab's snapshot. */
  }
  return snapshot;
}

export function canAttachSignupAttribution(createdAt: Date | null, snapshot: SignupAttribution, now = Date.now()) {
  if (!createdAt) return false;
  const created = createdAt.getTime();
  const captured = Date.parse(snapshot.captured_at);
  return (
    Number.isFinite(created) && Number.isFinite(captured) && captured <= created && created <= now && now - created <= 24 * 60 * 60 * 1000
  );
}
